import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { PilotAssignmentCommands } from '../../services/DispatcherActions';
import { OvertimeDialog } from './dialogs/OvertimeDialog';
import uuid from 'react-uuid';
import { Dialog } from '../../../../components/layout/dialogs/Dialog';
import { DialogProperties } from '../../../../components/layout/dialogs/DialogProperties';
import TitleContainer from '../../../../components/layout/TitleContainer';
import { formatStringToList, isNullOrEmpty, isObjectNull } from '../../../../components/helpers/ObjectHelpers';
import { formatMomentToDefault } from '../../../../components/helpers/DateTimeHelpers';
import { getPilotAssignmentCompensationId } from '../helpers/PilotAssignmentHelpers';
import { CardList } from '../../../../components/layout/card/CardList';
import Spacer from '../../../../components/layout/Spacer';
import { usePilotageOvertimeActivityType } from 'pages/pilot/hooks/usePilotageOvertimeActivityType';
import { useHasEditableChanged } from '../hooks/usePilotAssignmentStore';

export const OvertimeCard = (
    {
        ref,
        onPilotAssignmentChanged,
        assignmentService
    }) => {

    useImperativeHandle(ref, () => ({
        onPilotageChanged() {
            initialize();
        },
    }));

    const [isEditable, setIsEditable] = useState(false);
    const [compensation, setCompensation] = useState(null);
    const [showDialog, setShowDialog] = useState(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [isOvertimeAvailable, setIsOvertimeAvailable] = useState(false);
    const [cards, setCards] = useState([]);
    const [selectedCompensations, setSelectedCompensations] = useState([]);

    const listRef = useRef(null);

    const hasEditableChanged = useHasEditableChanged();

    useEffect(() => {
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if(!hasEditableChanged) return;
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasEditableChanged]);

    return (
        <>
            <TitleContainer
                title="Overtid"
                error={selectedCompensations.some(c => c.selected)}
                actions={getTitleActions()}
            />
            {isNullOrEmpty(cards) ? null : <Spacer height={30} /> }
            <CardList
                ref={listRef}
                source={cards}
                disabled={!isEditable}
                canShowNoContentMessage={false}
            />

            {
                showDialog &&
                <OvertimeDialog
                    onClose={() => setShowDialog(false)}
                    overtime={compensation}
                    isOvertimeAvailable={isOvertimeAvailable}
                    callback={onCallbackAsync}
                />
            }

            {
                showConfirmationDialog &&
                <Dialog
                    properties={{
                        ...DialogProperties,
                        mode: "confirmation",
                        title: "Slett overtid",
                        text: "&Oslash;nsker du &aring; slette overtiden?",
                        onClose: () => {
                            listRef.current.onReset(getPilotAssignmentCompensationId(compensation, "pilotagePilotOvertimeId"));
                            setShowConfirmationDialog(false);
                        }                                ,
                        onClick: onConfirmCallbackAsync
                    }}
                />
            }

        </>
    )

    function getTitleActions() {
        const actions = [];

        if (isEditable) {
            if (selectedCompensations.some(c => c.selected)) {
                actions.push(
                    {
                        icon: "trash",
                        onClick: onDeleteSelectedClick,
                        iconColor: "white"
                    });
            } else {
                actions.push(
                    {
                        icon: "plus",
                        onClick: () => {
                            setCompensation(null);
                            setShowDialog(true);
                        },
                        iconColor: "white"
                    });
            }
        }

        return actions;
    }

    function initialize() {

        const pilotAssignment = assignmentService.assignment;
        const isEditable = assignmentService.isEditable;
        setIsEditable(isEditable);
        setIsOvertimeAvailable(pilotAssignment.isOvertimeAvailable);

        const result = [];

        if (!isObjectNull(pilotAssignment.pilotagePilotCompensation)) {
            if (!isObjectNull(pilotAssignment.pilotagePilotCompensation.pilotagePilotOvertimes)) {
                pilotAssignment.pilotagePilotCompensation.pilotagePilotOvertimes.map((compensation) =>
                    result.push({
                        key: uuid(),
                        id: getPilotAssignmentCompensationId(compensation, "pilotagePilotOvertimeId"),
                        isDirty: compensation.pilotagePilotOvertimeId === 0,
                        title: getCompensationTimeDescription(compensation),
                        borderColor: "var(--card-swipe-border)",
                        backgroundColor: "var(--card-swipe-bg)",
                        canDelete: isEditable,
                        onDelete: isEditable ? () => onDeleteClick(compensation) : null,
                        onClick: compensation.isAutoGenerated ? null : isEditable ? () => {
                            setCompensation(compensation);
                            setShowDialog(true);
                            } : null,
                        content: <OvertimeContent compensation={compensation} />
                    }));
            }
        }

        setCards(result);
    }

    function onDeleteClick(compensation) {
        setCompensation(compensation);
        setShowConfirmationDialog(true);
    }

    // implement later when multible delete is fixed
    //function toggleSelectItem(id, selected) {
    //    const result = toggleSelectedItem(selectedCompensations, id, selected);
    //    setSelectedCompensations(result);
    //}

    function onDeleteSelectedClick() {
        setCompensation(null);
        setShowConfirmationDialog(true);
    }

    async function onCallbackAsync(obj) {

        switch (obj.command) {
            case PilotAssignmentCommands.AddPilotAssignmentOvertime:
            case PilotAssignmentCommands.UpdatePilotAssignmentOvertime:
                await assignmentService.onPutPilotAssignmentOvertimeAsync(obj);
                break;
            default:
                return;
        }

        setShowConfirmationDialog(false);
        onPilotAssignmentChanged();
        initialize();
    }

    async function onConfirmCallbackAsync() {

        if (isNullOrEmpty(compensation)) {

            await onDeletePilotAssignmentOvertimesAsync();
        } else {
            await assignmentService.onDeletePilotAssignmentOvertimeAsync(compensation);
        }

        setSelectedCompensations([]);
        setShowConfirmationDialog(false);
        onPilotAssignmentChanged();
        initialize();
    }
    async function onDeletePilotAssignmentOvertimesAsync() {
        for (const compensation of selectedCompensations) {
            if (!compensation.selected) continue;
            await assignmentService.onDeletePilotAssignmentOvertimeAsync(compensation);
        }
    }

    function getCompensationTimeDescription(compensation) {
        return `${formatMomentToDefault(compensation.fromTime)} &rarr; ${formatMomentToDefault(compensation.toTime)}`
    }
}

const OvertimeContent = ({ compensation }) => {
    const { activityType } = usePilotageOvertimeActivityType(compensation.pilotageOvertimeActivityType);
    return (
        <>
            {!isNullOrEmpty(activityType) && (
                <div>
                    <span className="pp-compensation-type">{activityType?.name}</span>
                </div>
            )}
            <div>{compensation.number} timer</div>
            {!isNullOrEmpty(compensation.description) && (
                <>
                    <Spacer height={5} />
                    <div>
                        <i>{formatStringToList(compensation.description)}</i>
                    </div>
                </>
            )}
        </>
    );
};
